import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Modal } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import MobileDataService from '../services/mobileDataService';
import { formatServicePriceShort, hasListedServicePrice } from '../utils/servicePriceDisplay';
import { emailService } from '../services/emailService';
import { smsService } from '../services/smsService';

interface Appointment {
  id: string;
  customerName: string;
  service: string;
  date: string;
  time: string;
  status: string;
  estimatedCost?: number;
  customerEmail?: string;
  customerPhone?: string;
}

interface SendInvoiceScreenProps {
  onBack: () => void;
  onNavigateToBusinessChat: (chatId: string, customerName: string) => void;
}

const SendInvoiceScreen: React.FC<SendInvoiceScreenProps> = ({ onBack, onNavigateToBusinessChat }) => {
  console.log('📸 SendInvoiceScreen rendered');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [invoicePrice, setInvoicePrice] = useState('');
  const [selectedService, setSelectedService] = useState<any>(null);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
  });
  const [services, setServices] = useState<any[]>([]);

  // Initialize data service
  const dataService = MobileDataService.getInstance();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const businessId = 'business_1';
      const localAppointments = dataService.getAppointments(businessId);
      const localServices = dataService.getServices(businessId);
      
      // Add demo phone numbers and emails to appointments for screenshots
      const appointmentsWithContactInfo = localAppointments.map(appointment => ({
        ...appointment,
        customerEmail: appointment.customerEmail || `${appointment.customerName.toLowerCase().replace(' ', '.')}@email.com`,
        customerPhone: appointment.customerPhone || '',
      }));
      
      // Add demo services if none exist
      const demoServices = localServices.length > 0 ? localServices : [
        { id: '1', name: 'Haircut & Styling', price: 75 },
        { id: '2', name: 'Hair Coloring', price: 120 },
        { id: '3', name: 'Manicure', price: 35 },
        { id: '4', name: 'Facial Treatment', price: 85 },
        { id: '5', name: 'Massage', price: 100 },
      ];
      
      setAppointments(appointmentsWithContactInfo);
      setServices(demoServices);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSelectAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setInvoicePrice(appointment.estimatedCost?.toString() || '');
  };

  const handleSelectService = (service: any) => {
    setSelectedService(service);
    setInvoicePrice(hasListedServicePrice(service.price) ? service.price.toString() : '');
    setShowServiceSelection(false);
  };

  const handleManualPriceEntry = () => {
    setSelectedService(null);
    setInvoicePrice('');
    setShowServiceSelection(false);
  };

  const handleCreateNewAppointment = () => {
    setShowCreateAppointment(true);
  };

  const handleCreateAppointment = () => {
    if (!newCustomerData.name || !newCustomerData.email || !newCustomerData.phone) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    // Create new appointment
    const newAppointment: Appointment = {
      id: `appointment_${Date.now()}`,
      customerName: newCustomerData.name,
      service: newCustomerData.service,
      date: newCustomerData.date,
      time: newCustomerData.time,
      status: 'pending',
      estimatedCost: 0,
      customerEmail: newCustomerData.email,
      customerPhone: newCustomerData.phone,
    };

    setAppointments([...appointments, newAppointment]);
    setSelectedAppointment(newAppointment);
    setShowCreateAppointment(false);
    setNewCustomerData({ name: '', email: '', phone: '', service: '', date: '', time: '' });
  };

  const handleSendInvoice = () => {
    if (!selectedAppointment) {
      Alert.alert('Error', 'Please select an appointment');
      return;
    }

    if (!invoicePrice || isNaN(Number(invoicePrice))) {
      Alert.alert('Error', 'Please enter a valid price');
      return;
    }

    // Show delivery method options
    Alert.alert(
      'Choose Delivery Method',
      'How would you like to send the invoice?',
      [
        { text: 'Email', onPress: () => sendInvoiceViaEmail() },
        { text: 'SMS', onPress: () => sendInvoiceViaSMS() },
        { text: 'In-App Chat', onPress: () => sendInvoiceViaChat() },
        { text: 'Cancel', style: 'cancel' },
      ]
    );
  };

  const sendInvoiceViaEmail = async () => {
    if (!selectedAppointment?.customerEmail) {
      Alert.alert('Error', 'Customer email not available');
      return;
    }

    try {
      // For demo/screenshots, simulate email sending
      console.log('📧 Demo: Sending invoice via email to:', selectedAppointment.customerEmail);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
      Alert.alert('Success', `Invoice sent via email to ${selectedAppointment.customerEmail}!`);
    } catch (error) {
      Alert.alert('Error', 'Failed to send invoice via email');
    }
  };

  const sendInvoiceViaSMS = async () => {
    if (!selectedAppointment?.customerPhone) {
      Alert.alert('Error', 'Customer phone number not available');
      return;
    }

    try {
      // For demo/screenshots, simulate SMS sending
      console.log('📱 Demo: Sending invoice via SMS to:', selectedAppointment.customerPhone);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
      Alert.alert('Success', `Invoice sent via SMS to ${selectedAppointment.customerPhone}!`);
    } catch (error) {
      Alert.alert('Error', 'Failed to send invoice via SMS');
    }
  };

  const sendInvoiceViaChat = () => {
    if (!selectedAppointment) return;
    
    // For demo/screenshots, simulate chat navigation
    console.log('💬 Demo: Navigating to chat for invoice');
    Alert.alert('Demo Mode', 'In production, this would open the chat screen to send the invoice. For now, showing success message.', [
      { text: 'OK', onPress: () => {
        const chatId = `chat_${selectedAppointment.id}`;
        onNavigateToBusinessChat(chatId, selectedAppointment.customerName);
      }}
    ]);
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Send Invoice</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content}>
        {/* Step 1: Select Appointment */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Step 1: Select Appointment</Text>
          
          {appointments.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>No appointments available</Text>
              <TouchableOpacity 
                style={styles.createButton}
                onPress={handleCreateNewAppointment}
              >
                <Text style={styles.createButtonText}>Create New Appointment</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.appointmentsList}>
              {appointments.map((appointment) => (
                <TouchableOpacity
                  key={appointment.id}
                  style={[
                    styles.appointmentCard,
                    selectedAppointment?.id === appointment.id && styles.selectedAppointment
                  ]}
                  onPress={() => handleSelectAppointment(appointment)}
                >
                  <Text style={styles.customerName}>{appointment.customerName}</Text>
                  <Text style={styles.serviceName}>{appointment.service}</Text>
                  <Text style={styles.appointmentDate}>
                    {appointment.date} at {appointment.time}
                  </Text>
                  <Text style={styles.appointmentStatus}>{appointment.status}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Step 2: Set Price */}
        {selectedAppointment && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Step 2: Set Invoice Amount</Text>
            
            <View style={styles.priceOptions}>
              <TouchableOpacity
                style={styles.priceOption}
                onPress={() => setShowServiceSelection(true)}
              >
                <Text style={styles.priceOptionText}>Select from Services</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.priceOption}
                onPress={handleManualPriceEntry}
              >
                <Text style={styles.priceOptionText}>Enter Manual Price</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.priceInputContainer}>
              <Text style={styles.priceLabel}>Invoice Amount ($)</Text>
              <AppTextInput
                style={styles.priceInput}
                value={invoicePrice}
                onChangeText={setInvoicePrice}
                placeholder="Enter amount"
                keyboardType="numeric"
              />
            </View>

            {selectedService && (
              <View style={styles.selectedServiceInfo}>
                <Text style={styles.selectedServiceText}>
                  Selected: {selectedService.name} — {formatServicePriceShort(selectedService.price)}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Step 3: Send Invoice */}
        {selectedAppointment && invoicePrice && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Step 3: Send Invoice</Text>
            
            <View style={styles.invoiceSummary}>
              <Text style={styles.summaryTitle}>Invoice Summary</Text>
              <Text style={styles.summaryText}>Customer: {selectedAppointment.customerName}</Text>
              <Text style={styles.summaryText}>Service: {selectedAppointment.service}</Text>
              <Text style={styles.summaryText}>Amount: ${invoicePrice}</Text>
              <Text style={styles.summaryText}>Date: {selectedAppointment.date} at {selectedAppointment.time}</Text>
            </View>

            <TouchableOpacity
              style={styles.sendButton}
              onPress={handleSendInvoice}
            >
              <Text style={styles.sendButtonText}>Send Invoice</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      {/* Service Selection Modal */}
      <Modal
        visible={showServiceSelection}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Service</Text>
            <ScrollView style={styles.servicesList}>
              {services.map((service) => (
                <TouchableOpacity
                  key={service.id}
                  style={styles.serviceItem}
                  onPress={() => handleSelectService(service)}
                >
                  <Text style={styles.serviceName}>{service.name}</Text>
                  <Text style={styles.servicePrice}>{formatServicePriceShort(service.price)}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowServiceSelection(false)}
            >
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Create New Appointment Modal */}
      <Modal
        visible={showCreateAppointment}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Create New Appointment</Text>
            
            <AppTextInput
              style={styles.input}
              placeholder="Customer Name"
              value={newCustomerData.name}
              onChangeText={(text) => setNewCustomerData({...newCustomerData, name: text})}
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Email"
              value={newCustomerData.email}
              onChangeText={(text) => setNewCustomerData({...newCustomerData, email: text})}
              keyboardType="email-address"
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Phone"
              value={newCustomerData.phone}
              onChangeText={(text) => setNewCustomerData({...newCustomerData, phone: text})}
              keyboardType="phone-pad"
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Service"
              value={newCustomerData.service}
              onChangeText={(text) => setNewCustomerData({...newCustomerData, service: text})}
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Date (YYYY-MM-DD)"
              value={newCustomerData.date}
              onChangeText={(text) => setNewCustomerData({...newCustomerData, date: text})}
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Time (HH:MM)"
              value={newCustomerData.time}
              onChangeText={(text) => setNewCustomerData({...newCustomerData, time: text})}
            />

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowCreateAppointment(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.createButton}
                onPress={handleCreateAppointment}
              >
                <Text style={styles.createButtonText}>Create</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 50,
    paddingBottom: 15,
    backgroundColor: '#007AFF',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
    minWidth: 60,
  },
  backButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
    textAlign: 'center',
  },
  placeholder: {
    width: 60,
  },
  content: {
    flex: 1,
    padding: 15,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  emptyState: {
    alignItems: 'center',
    padding: 25,
    backgroundColor: 'white',
    borderRadius: 12,
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 15,
  },
  createButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  createButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  appointmentsList: {
    gap: 8,
  },
  appointmentCard: {
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedAppointment: {
    borderColor: '#007AFF',
    backgroundColor: '#f0f8ff',
  },
  customerName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 3,
  },
  serviceName: {
    fontSize: 12,
    color: '#666',
    marginBottom: 3,
  },
  appointmentDate: {
    fontSize: 12,
    color: '#666',
    marginBottom: 3,
  },
  appointmentStatus: {
    fontSize: 11,
    color: '#007AFF',
    fontWeight: '600',
  },
  priceOptions: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 15,
  },
  priceOption: {
    flex: 1,
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  priceOptionText: {
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },
  priceInputContainer: {
    marginBottom: 12,
  },
  priceLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  priceInput: {
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    fontSize: 14,
  },
  selectedServiceInfo: {
    backgroundColor: '#f0f8ff',
    padding: 8,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
  },
  selectedServiceText: {
    fontSize: 12,
    color: '#007AFF',
    fontWeight: '500',
  },
  invoiceSummary: {
    backgroundColor: 'white',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  summaryText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 6,
  },
  sendButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  sendButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  servicesList: {
    maxHeight: 300,
  },
  serviceItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  servicePrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  closeButton: {
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 15,
  },
  closeButtonText: {
    color: '#333',
    fontSize: 16,
    fontWeight: '600',
  },
  input: {
    backgroundColor: '#f5f5f5',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    fontSize: 16,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#333',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default SendInvoiceScreen;
